/* eslint-disable @typescript-eslint/consistent-type-imports */
import { Injectable } from '@angular/core';
import { map, mergeMap, type Observable, throwError } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from './storage.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { PaymentusRequest } from '../models/paymentus';
import { UtilService } from './util.service';
import { StorageEnum } from '../enums/storage.enum';

@Injectable({
  providedIn: 'root',
})
export class PaymentusService {
  private static readonly IOS_WEB_HOST_PROD = 'manage.gassouth.com';
  private static readonly IOS_WEB_HOST_NON_PROD = 'qa-oas-redesign.gassouth.com';

  constructor(
    private readonly http: HttpClient,
    private readonly storage: StorageService,
    private readonly util: UtilService,
  ) {}

  sanitizeNameField(value: unknown): string {
    return String(value ?? '').replace(/[^A-Za-z0-9. ]/g, '');
  }

  getPaymentusToken(paymentusRequest: PaymentusRequest): Observable<any> {
    const tokenUrl = environment.OASUrl() + '/paymentus/authtoken';

    const JWTRes: any = this.http.get(tokenUrl, { responseType: 'text' });

    return JWTRes.pipe(
      mergeMap((data: string) => {
        if (!this.util.isNullOrWhitespace(data)) {
          return this.savePayment(paymentusRequest, data);
        } else {
          return throwError(() => new Error('Paymentus token error: Failed to get token'));
        }
      }),
    );
  }

  getPaymentusTokenPixel(): Observable<any> {
    const tokenUrl = environment.OASUrl() + '/paymentus/authtokenpixel';
    const PixelSdkRes: any = this.http.get(tokenUrl, { responseType: 'text' });
    return PixelSdkRes;
  }

  savePayment(paymentusRequest: PaymentusRequest, JWTRes: any): Observable<any> {
    const paymentusTokenUrl = environment.paymentusTokenUrl();
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${JWTRes}`,
      }),
    };

    const res = this.http.post(paymentusTokenUrl, { profile: paymentusRequest }, httpOptions);

    let paymentusErrors: any[] = [];
    paymentusErrors = this.storage.getSession(StorageEnum.GlobalVars).paymentusErrors;

    return res.pipe(
      map((data: any) => {
        if (data['profile-response'].status === 'PROCESSED') {
          return data;
        } else if (data['profile-response'].status === 'FAILED') {
          let paymentusErrorMessage = '';
          if (Array.isArray(data['profile-response'].errors.error)) {
            paymentusErrorMessage = data['profile-response'].errors.error[0].value;
          } else {
            paymentusErrorMessage = data['profile-response'].errors.error.value;
          }

          // log error into Application insights using OAS API call
          this.logTokenError(paymentusRequest['user-info']?.['login-id'], paymentusErrorMessage);

          // get display message from Builder
          const newPaymentusError = paymentusErrors.find((error) => {
            return (
              error.paymentusError.errorMessage.toLowerCase().replaceAll('.', '') ===
              paymentusErrorMessage.toLowerCase().replaceAll('.', '')
            );
          });
          // build Message and Code string
          throw new Error(
            newPaymentusError.paymentusError.errorCode + '|' + newPaymentusError.paymentusError.displayMessage,
          );
        }
      }),
    );
  }

  /**
   * Get a digital payment token from Paymentus for Google Pay, Apple Pay, PayPal, or Venmo.
   *
   * @param accountNumber - The customer's account number
   * @param pmCategory - Payment method category (GP, AP, PAYPAL_ACCOUNT, VENMO)
   * @param postMessagePmDetailsOrigin - Origin URL for postMessage flow (web only)
   * @param platform - Optional platform identifier ('ios' | 'android') for mobile redirect flow
   * @returns Observable with the SSO token
   */
  getDigitalPaymentToken(
    accountNumber: string,
    pmCategory: string,
    postMessagePmDetailsOrigin: string,
    platform?: 'ios' | 'android',
  ): Observable<any> {
    const normalizedOrigin = this.normalizeDigitalPaymentOrigin(postMessagePmDetailsOrigin, platform);
    let params =
      '/paymentus/digitaltokenpostmessage?ownerId=' +
      accountNumber +
      '&pmCategory=' +
      pmCategory +
      '&postMessagePmDetailsOrigin=' +
      normalizedOrigin;

    // Add platform parameter for mobile - backend will use postbackUrl instead of postMessage
    if (platform) {
      params += '&platform=' + platform;
    }

    const url = environment.OASUrl() + params;
    const res = this.http.get(url, { responseType: 'text' });
    return res;
  }

  private normalizeDigitalPaymentOrigin(origin: string, platform?: 'ios' | 'android'): string {
    const trimmedOrigin = origin?.trim() ?? '';
    if (!trimmedOrigin) {
      return trimmedOrigin;
    }

    if (platform !== 'ios' || !trimmedOrigin.toLowerCase().startsWith('capacitor://')) {
      return trimmedOrigin;
    }

    let routePath = '';
    try {
      const parsedUrl = new URL(trimmedOrigin);
      routePath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch {
      const firstPathSlash = trimmedOrigin.indexOf('/', 'capacitor://'.length);
      routePath = firstPathSlash >= 0 ? trimmedOrigin.slice(firstPathSlash) : '';
    }

    const host = environment.isProduction ? PaymentusService.IOS_WEB_HOST_PROD : PaymentusService.IOS_WEB_HOST_NON_PROD;

    return `https://${host}${routePath}`;
  }

  logTokenError(accountNumber?: string, errorMessage?: string): void {
    const url =
      environment.OASUrl() +
      '/paymentus/logtokenerror?accountNumber=' +
      accountNumber +
      '&errorMessage=' +
      errorMessage;
    this.http.get(url, { responseType: 'text' });
  }
}
